import { FC, useCallback, useEffect, useRef, useState, useMemo, forwardRef, ForwardedRef } from "react";
import styled, { css, useTheme } from "styled-components";
import {Colors, CommonInput, FInter, FlexBox, Fonts, INPUT_TYPES, useBreakpoint} from "@brg/ui";
import { useTranslation } from "react-i18next";
import {API, Designer, Tag, TagsAPI} from "@brg/api";
import {useParams} from "react-router-dom";
import { LayoutConst } from "core";
import { useController } from "react-hook-form";

export type AuthorT = Designer<'Local'> & { disabled?: boolean };

export type AuthorActionsT = {
  action: string,
  removedValue?: AuthorT
}

export const SearchWrapper = styled(FlexBox)<{ out?: boolean, footer?: boolean, isOpen?: boolean, isLMS?: boolean }>`
  margin-left: 0;
  position: relative;
  width: 100%;
`;

export const NavbarResultsWrapper = styled.div<{footer?: boolean, isLMS?: boolean}>`
  // position: absolute;
  background-color: ${(p) => p?.isLMS ? '#FEFEFE' : p.theme.mainSearchBar.input.bg};
  max-height: 200px;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.12);
  margin-bottom: 50px;

  ::-webkit-scrollbar {
    width: 3px; /* ширина scrollbar */
    height: 68px;
  }
  ::-webkit-scrollbar-track {
      background: transparent; /* цвет дорожки */
  }
  ::-webkit-scrollbar-thumb {
      border-radius: 10px; /* закругления плашки */
      background-color: ${({ theme }) => theme.scroll.bg};
  }

  .save-msg {
    font-size: 17px;
    line-height: 22px;

    * {
      color: ${(p) => p.theme.educationSearchBar.result.color};
    }

    span:last-child {
      text-decoration: underline;
    }
  }
`;

export const NavbarResultsItem = styled.div<{ isSelected?: boolean, isLMS?: boolean }>`
  display: flex;
  width: 100%;
  height: 46px;
  cursor: pointer;
  padding: 12px 16px;
  transition: background-color 0.2s;
  background-color: ${(p) => p?.isLMS ? '#FEFEFE' : p.theme.mainSearchBar.input.bg};

  :not(.save-msg) {
    :hover {
      background-color: ${(p) => p.theme.app.ten};
    }
    :active {
      background-color: ${Colors.galBlack31};
    }
  }

  .check-icon {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
  }

  span {
    font-size: 13px;
    line-height: 16px;
  }
`;

const DropWrapper = styled.div<{ orientation?: string, isLMS?: boolean }>`
  position: absolute;
  top: 40px;
  left: 0;
  height: auto;
  width: 100%;
  z-index: 400;

  .search-msg {
    color: ${({ theme }) => theme.projectEdit.mobile.inputs.color};
    ${({ isLMS }) => isLMS && css`
      background-color: #FEFEFE;
    `};
  }

  .item {
    :not(.search-msg) {
      :hover {
        background-color: ${({ theme, isLMS }) => isLMS ? '#F2F2F2' : theme.mainSearchBar.text.hover.color};
      }
    }
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    top: 40px;
  }

`;

const InputWrapper = styled(FlexBox)<{isPresentationLMS?: boolean}>`
  position: relative;
  width: 100%;

  svg path {
    cursor: pointer;
    fill: ${Colors.galBlack47};
    transition: color 0.2s;
  }
  .close-btn-wrap {
    &:hover {
      svg path {
        fill: ${Colors.galBlack100};
        transition: color 0.2s;
      }
    }
  }

  input {
    max-width: 700px;
    width: 700px;
    padding: 0 16px;

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      max-width: 564px;
      width: 564px;
    }

    @media (max-width: ${LayoutConst.screen.m}px) {
      max-width: none;
      width: 100%;
      min-width: 100%;
    }
  }

`;

const Text = styled(FInter.SmallTextDiv)`
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: -0.02em;
`;

export interface MainMenuSearchBarProps {
  onChange?: (value: any) => void,
  onSelect?: (id: string) => void,
  required?: boolean;
  label?: string;
  value?: string
  placeholder?: string;
  inputProps?: any;
}

export const useDebouncedEffect = (effect: any, deps: any, delay: number) => {
  useEffect(() => {
    const handler = setTimeout(() => effect(), delay);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(deps || []), delay]);
};

const mergeRefs = (...refs: (any)[]) => {
  return (node: any) => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref != null) {
        (ref as any).current = node;
      }
    });
  };
}

export const UniversitySearchBar = forwardRef<HTMLInputElement, MainMenuSearchBarProps>((props,ref) => {
  const searchRef = useRef<any>(null);
  const resultRef = useRef<any>(null);
  const theme = useTheme();
  const isMobile = useBreakpoint(LayoutConst.screen.s -1 );
  const [values, setValues] = useState<Tag[]>([]);
  const { i18n, t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [currentValue, setCurrentValue] = useState('')
  const [isEmpty, setIsEmpty] = useState(false);
  const { id } = useParams<{ id: string }>();

  const {
    placeholder,
    label,
    required,  
    onChange,
    onSelect,
    value,
    ...rest
  } = props

  const UNIVER_ID = '009c939ebdc34eb4801246557e49979f';

  useDebouncedEffect(
    () => {
      if (searchText.length >= 2) {

        TagsAPI.GetTagsTree({ search: searchText, groupIds: [UNIVER_ID], isHeaderIgnored: true })
          .then((v) => v.json())
          .then((v) => API.toLang<Tag[]>(v, i18n.language))
          .then((v) => {
              const result = v?.[0]?.additionalTags;
              if (result) {
                setValues(result);
              }
              setIsEmpty(result?.length < 1 || !result);
          });
      } else {
        setValues([]);
      }
    },
    [searchText, i18n.language, id],
    700
  );

  const resetSearchMode = () => {
    setSearchText('');
  }

  const resetText = () => {
    setSearchText('');
    setValues([])
  }

  const handleAddClick = (data: Tag ) => {
    const { name, tagId } = data;
    setCurrentValue(name)
    onChange?.(name)
    resetSearchMode(); 
  }


  const handleClose = (ev: any) => {
    if (searchRef?.current && !searchRef?.current?.contains(ev?.target) && typeof ev?.target?.className === 'string' && !ev?.target?.className?.includes('save-education-center')) {
      resetSearchMode();
    }
  }

  useEffect(() => {
    window.addEventListener('click', handleClose)
    return () => {
      window.removeEventListener('click', handleClose)
    }
  }, [])

  const onSaveEducationNameClick = useCallback(() => {
    onChange?.(currentValue)
    resetText();
  }, [currentValue, resetText])

  const SaveEducationNameButton = useMemo(() => {
    return i18n.language === 'ru' ? (
      <span onClick={onSaveEducationNameClick} className="save-education-center">Сохранить</span>
    ) : (
      <span onClick={onSaveEducationNameClick} className="save-education-center">Save</span>
    )
  }, [i18n, onSaveEducationNameClick])

  return (
      <SearchWrapper isOpen={searchText.length > 0}>
        <CommonInput 
          ref={mergeRefs(searchRef, ref)}
          type={INPUT_TYPES.search}
          value={currentValue}
          label={label}
          required={required}
          placeholder={placeholder}
          onChange={(e: any) => {
            setSearchText(e.target.value)
            setCurrentValue(e.target.value)
            onChange?.(e)
          }}
          resultComponent={(
            <>
              {searchText.length > 0 ? (
                <DropWrapper>
                  <NavbarResultsWrapper ref={resultRef}>
                    {values?.map((v) => {
                      const cv = v
                      const isSelected = onSelect && onSelect(v?.tagId);
                      return (
                        <NavbarResultsItem
                          onClick={() => {
                            handleAddClick(v)
                          }}
                          key={v?.tagId}
                          style={{ marginBottom: '4px' }}
                          className={'item'}
                        >
                          <FlexBox align={"center"} gx={8} style={{ padding: "2px 0 2px 9px", position: 'relative' }} width={'100%'}>
                            <Text color={theme.mainSearchBar.text.color}>{cv.name}</Text>
                            {!!isSelected && (
                              <svg className={'check-icon'} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12.7589 5.22636C13.0803 5.52689 13.0803 6.01832 12.7589 6.32014L6.92932 11.773C6.76865 11.9239 6.55856 12 6.34708 12C6.13697 12 5.92682 11.9239 5.76614 11.773L3.24204 9.42346C2.91932 9.12293 2.91932 8.63151 3.24204 8.32969C3.56339 8.02787 4.08387 8.02787 4.40522 8.32969L6.34708 10.1311L11.5944 5.22636C11.9157 4.92455 12.4362 4.92455 12.7589 5.22636Z" fill={theme.mainSearchBar.text.color || '#151514'} />
                              </svg>
                            )}
                          </FlexBox>
                        </NavbarResultsItem>
                      );
                    })}
                    {values.length < 1 && searchText.length < 2 && (
                      <NavbarResultsItem className="search-msg">
                        <FlexBox
                          align={"center"}
                          width={"100%"}
                          justify={"center"}
                          gx={8}
                        >
                          <Text>
                            {t("text.more2chars")}
                          </Text>
                        </FlexBox>
                      </NavbarResultsItem>
                    )}
                    {values.length < 1 && searchText.length >= 2 && !isEmpty && (
                      <NavbarResultsItem className="search-msg">
                        <FlexBox
                          align={"center"}
                          width={"100%"}
                          justify={"center"}
                          gx={8}
                        >
                          <Text>{t("text.loading")}</Text>
                        </FlexBox>
                      </NavbarResultsItem>
                    )}
                    {values.length < 1 && searchText.length >= 2 && isEmpty && (
                      <NavbarResultsItem className="save-msg">
                        <FlexBox
                          align={"center"}
                          width={"100%"}
                          justify={"start"}
                          direction="row"
                          gx={2}
                          style={{ whiteSpace: isMobile ? 'wrap' : 'nowrap' }}
                        >
                          <span>
                            {t("text.notfound_university")}
                          </span>
                          {SaveEducationNameButton}
                        </FlexBox>
                      </NavbarResultsItem>
                    )}
                  </NavbarResultsWrapper>
                </DropWrapper>
              ) : null}
            </>
          )}
        />
      </SearchWrapper>
  );
})
import { FC, useCallback, useEffect, useRef, useState, useMemo, forwardRef, ForwardedRef } from "react";
import styled, { css, useTheme } from "styled-components";
import {Colors, CommonInput, FInter, FlexBox, Fonts, INPUT_TYPES, useBreakpoint} from "@brg/ui";
import { useTranslation } from "react-i18next";
import {API, Designer, Tag, TagsAPI} from "@brg/api";
import {useParams} from "react-router-dom";
import { LayoutConst } from "core";
import { useController } from "react-hook-form";

export type AuthorT = Designer<'Local'> & { disabled?: boolean };

export type AuthorActionsT = {
  action: string,
  removedValue?: AuthorT
}

export const SearchWrapper = styled(FlexBox)<{ out?: boolean, footer?: boolean, isOpen?: boolean, isLMS?: boolean }>`
  margin-left: 0;
  position: relative;
  width: 100%;
`;

export const NavbarResultsWrapper = styled.div<{footer?: boolean, isLMS?: boolean}>`
  // position: absolute;
  background-color: ${(p) => p?.isLMS ? '#FEFEFE' : p.theme.mainSearchBar.input.bg};
  max-height: 200px;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.12);
  margin-bottom: 50px;

  ::-webkit-scrollbar {
    width: 3px; /* ширина scrollbar */
    height: 68px;
  }
  ::-webkit-scrollbar-track {
      background: transparent; /* цвет дорожки */
  }
  ::-webkit-scrollbar-thumb {
      border-radius: 10px; /* закругления плашки */
      background-color: ${({ theme }) => theme.scroll.bg};
  }

  .save-msg {
    font-size: 17px;
    line-height: 22px;

    * {
      color: ${(p) => p.theme.educationSearchBar.result.color};
    }

    span:last-child {
      text-decoration: underline;
    }
  }
`;

export const NavbarResultsItem = styled.div<{ isSelected?: boolean, isLMS?: boolean }>`
  display: flex;
  width: 100%;
  height: 46px;
  cursor: pointer;
  padding: 12px 16px;
  transition: background-color 0.2s;
  background-color: ${(p) => p?.isLMS ? '#FEFEFE' : p.theme.mainSearchBar.input.bg};

  :not(.save-msg) {
    :hover {
      background-color: ${(p) => p.theme.app.ten};
    }
    :active {
      background-color: ${Colors.galBlack31};
    }
  }

  .check-icon {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
  }

  span {
    font-size: 13px;
    line-height: 16px;
  }
`;

const DropWrapper = styled.div<{ orientation?: string, isLMS?: boolean }>`
  position: absolute;
  top: 40px;
  left: 0;
  height: auto;
  width: 100%;
  z-index: 400;

  .search-msg {
    color: ${({ theme }) => theme.projectEdit.mobile.inputs.color};
    ${({ isLMS }) => isLMS && css`
      background-color: #FEFEFE;
    `};
  }

  .item {
    :not(.search-msg) {
      :hover {
        background-color: ${({ theme, isLMS }) => isLMS ? '#F2F2F2' : theme.mainSearchBar.text.hover.color};
      }
    }
  }

  @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
    top: 40px;
  }

`;

const InputWrapper = styled(FlexBox)<{isPresentationLMS?: boolean}>`
  position: relative;
  width: 100%;

  svg path {
    cursor: pointer;
    fill: ${Colors.galBlack47};
    transition: color 0.2s;
  }
  .close-btn-wrap {
    &:hover {
      svg path {
        fill: ${Colors.galBlack100};
        transition: color 0.2s;
      }
    }
  }

  input {
    max-width: 700px;
    width: 700px;
    padding: 0 16px;

    @media (max-width: ${LayoutConst.screen.mac13}px), (max-height: ${LayoutConst.screen.height.projectCover}px) {
      max-width: 564px;
      width: 564px;
    }

    @media (max-width: ${LayoutConst.screen.m}px) {
      max-width: none;
      width: 100%;
      min-width: 100%;
    }
  }

`;

const Text = styled(FInter.SmallTextDiv)`
  font-weight: 400;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: -0.02em;
`;

export interface MainMenuSearchBarProps {
  onChange?: (value: any) => void,
  onSelect?: (id: string) => void,
  required?: boolean;
  label?: string;
  value?: string
  placeholder?: string;
  inputProps?: any;
}

export const useDebouncedEffect = (effect: any, deps: any, delay: number) => {
  useEffect(() => {
    const handler = setTimeout(() => effect(), delay);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...(deps || []), delay]);
};

const mergeRefs = (...refs: (any)[]) => {
  return (node: any) => {
    refs.forEach(ref => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref != null) {
        (ref as any).current = node;
      }
    });
  };
}

export const UniversitySearchBar = forwardRef<HTMLInputElement, MainMenuSearchBarProps>((props,ref) => {
  const searchRef = useRef<any>(null);
  const resultRef = useRef<any>(null);
  const theme = useTheme();
  const isMobile = useBreakpoint(LayoutConst.screen.s -1 );
  const [values, setValues] = useState<Tag[]>([]);
  const { i18n, t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [currentValue, setCurrentValue] = useState('')
  const [isEmpty, setIsEmpty] = useState(false);
  const { id } = useParams<{ id: string }>();

  const {
    placeholder,
    label,
    required,  
    onChange,
    onSelect,
    value,
    ...rest
  } = props

  const UNIVER_ID = '009c939ebdc34eb4801246557e49979f';

  useDebouncedEffect(
    () => {
      if (searchText.length >= 2) {

        TagsAPI.GetTagsTree({ search: searchText, groupIds: [UNIVER_ID], isHeaderIgnored: true })
          .then((v) => v.json())
          .then((v) => API.toLang<Tag[]>(v, i18n.language))
          .then((v) => {
              const result = v?.[0]?.additionalTags;
              if (result) {
                setValues(result);
              }
              setIsEmpty(result?.length < 1 || !result);
          });
      } else {
        setValues([]);
      }
    },
    [searchText, i18n.language, id],
    700
  );

  const resetSearchMode = () => {
    setSearchText('');
  }

  const resetText = () => {
    setSearchText('');
    setValues([])
  }

  const handleAddClick = (data: Tag ) => {
    const { name, tagId } = data;
    setCurrentValue(name)
    onChange?.(name)
    resetSearchMode(); 
  }


  const handleClose = (ev: any) => {
    if (searchRef?.current && !searchRef?.current?.contains(ev?.target) && typeof ev?.target?.className === 'string' && !ev?.target?.className?.includes('save-education-center')) {
      resetSearchMode();
    }
  }

  useEffect(() => {
    window.addEventListener('click', handleClose)
    return () => {
      window.removeEventListener('click', handleClose)
    }
  }, [])

  const onSaveEducationNameClick = useCallback(() => {
    onChange?.(currentValue)
    resetText();
  }, [currentValue, resetText])

  const SaveEducationNameButton = useMemo(() => {
    return i18n.language === 'ru' ? (
      <span onClick={onSaveEducationNameClick} className="save-education-center">Сохранить</span>
    ) : (
      <span onClick={onSaveEducationNameClick} className="save-education-center">Save</span>
    )
  }, [i18n, onSaveEducationNameClick])

  return (
      <SearchWrapper isOpen={searchText.length > 0}>
        <CommonInput 
          ref={mergeRefs(searchRef, ref)}
          type={INPUT_TYPES.search}
          value={currentValue}
          label={label}
          required={required}
          placeholder={placeholder}
          onChange={(e: any) => {
            setSearchText(e.target.value)
            setCurrentValue(e.target.value)
            onChange?.(e)
          }}
          resultComponent={(
            <>
              {searchText.length > 0 ? (
                <DropWrapper>
                  <NavbarResultsWrapper ref={resultRef}>
                    {values?.map((v) => {
                      const cv = v
                      const isSelected = onSelect && onSelect(v?.tagId);
                      return (
                        <NavbarResultsItem
                          onClick={() => {
                            handleAddClick(v)
                          }}
                          key={v?.tagId}
                          style={{ marginBottom: '4px' }}
                          className={'item'}
                        >
                          <FlexBox align={"center"} gx={8} style={{ padding: "2px 0 2px 9px", position: 'relative' }} width={'100%'}>
                            <Text color={theme.mainSearchBar.text.color}>{cv.name}</Text>
                            {!!isSelected && (
                              <svg className={'check-icon'} width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12.7589 5.22636C13.0803 5.52689 13.0803 6.01832 12.7589 6.32014L6.92932 11.773C6.76865 11.9239 6.55856 12 6.34708 12C6.13697 12 5.92682 11.9239 5.76614 11.773L3.24204 9.42346C2.91932 9.12293 2.91932 8.63151 3.24204 8.32969C3.56339 8.02787 4.08387 8.02787 4.40522 8.32969L6.34708 10.1311L11.5944 5.22636C11.9157 4.92455 12.4362 4.92455 12.7589 5.22636Z" fill={theme.mainSearchBar.text.color || '#151514'} />
                              </svg>
                            )}
                          </FlexBox>
                        </NavbarResultsItem>
                      );
                    })}
                    {values.length < 1 && searchText.length < 2 && (
                      <NavbarResultsItem className="search-msg">
                        <FlexBox
                          align={"center"}
                          width={"100%"}
                          justify={"center"}
                          gx={8}
                        >
                          <Text>
                            {t("text.more2chars")}
                          </Text>
                        </FlexBox>
                      </NavbarResultsItem>
                    )}
                    {values.length < 1 && searchText.length >= 2 && !isEmpty && (
                      <NavbarResultsItem className="search-msg">
                        <FlexBox
                          align={"center"}
                          width={"100%"}
                          justify={"center"}
                          gx={8}
                        >
                          <Text>{t("text.loading")}</Text>
                        </FlexBox>
                      </NavbarResultsItem>
                    )}
                    {values.length < 1 && searchText.length >= 2 && isEmpty && (
                      <NavbarResultsItem className="save-msg">
                        <FlexBox
                          align={"center"}
                          width={"100%"}
                          justify={"start"}
                          direction="row"
                          gx={2}
                          style={{ whiteSpace: isMobile ? 'wrap' : 'nowrap' }}
                        >
                          <span>
                            {t("text.notfound_university")}
                          </span>
                          {SaveEducationNameButton}
                        </FlexBox>
                      </NavbarResultsItem>
                    )}
                  </NavbarResultsWrapper>
                </DropWrapper>
              ) : null}
            </>
          )}
        />
      </SearchWrapper>
  );
})
